/* Campaign presentation only. The server-side discount remains authoritative. */
(() => {
  if (customElements.get('rdtn-mix3-campaign')) return;
  const ownSelector = '[data-rdtn-mix3-placement]';
  const cartSelector = '#cart-drawer, cart-drawer-component, .cart-items-component, .cart-totals, .cart__ctas, [data-template="cart"]';
  const getChassis = (title = '', variant = '') => {
    const text = `${title} ${variant}`.toUpperCase();
    if (/\bZN6\b|\b(?:TOYOTA|GT)\s?86\b|\bBRZ\b|\bFR-S\b/.test(text)) return ['zn6', 'Toyota 86'];
    if (/\bZ3\b/.test(text)) return ['z3', 'Z3'];
    if (/\bE9[0-3X]\b/.test(text)) return ['e9x', 'E9X'];
    const matches = [...new Set(text.match(/\bE(?:30|32|34|36|38|39|46|53)\b/g) || [])];
    if (matches.length === 2 && matches.includes('E32') && matches.includes('E34')) return ['e34', 'E32/E34'];
    return matches.length === 1 ? [matches[0].toLowerCase(), matches[0] === 'E53' ? 'E53 X5' : matches[0]] : null;
  };
  class RenditionMix3Campaign extends HTMLElement {
    connectedCallback() { this.initFrame = requestAnimationFrame(() => this.initialize()); }
    initialize() {
      if (!this.isConnected || this.started) return;
      const json = this.querySelector('[data-mix3-config]');
      if (!json) return;
      try { this.config = JSON.parse(json.textContent); } catch { return; }
      this.started = true;
      this.mountedNodes = new Set();
      this.cart = this.config.cart;
      this.abort = new AbortController();
      this.serial = 0;
      this.start = () => {
        this.mount();
        this.observer = new MutationObserver((records) => {
          const relevant = records.filter((record) => {
            const el = record.target.nodeType === 1 ? record.target : record.target.parentElement;
            return el && !el.closest(ownSelector) && !this.contains(el);
          });
          if (!relevant.length) return;
          if (!this.mountQueued) {
            this.mountQueued = true;
            requestAnimationFrame(() => { this.mountQueued = false; this.mount(); });
          }
          if (relevant.some((record) => {
            const el = record.target.nodeType === 1 ? record.target : record.target.parentElement;
            if (el?.closest(cartSelector)) return true;
            return [...record.addedNodes].some((node) => node.nodeType === 1 && !node.matches(ownSelector) && (node.matches(cartSelector) || node.querySelector(cartSelector)));
          })) this.queueRefresh();
        });
        this.observer.observe(document.body, { childList: true, subtree: true, characterData: true });
        for (const event of ['cart:update', 'cart:updated', 'cart:change', 'cart:refresh']) document.addEventListener(event, () => this.queueRefresh(), { signal: this.abort.signal });
        window.addEventListener('pageshow', (event) => { if (event.persisted) this.queueRefresh(); }, { signal: this.abort.signal });
        document.addEventListener('shopify:section:load', () => this.mount(), { signal: this.abort.signal });
      };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', this.start, { once: true, signal: this.abort.signal });
      else this.start();
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.initFrame);
      this.abort?.abort();
      this.request?.abort();
      this.observer?.disconnect();
      clearTimeout(this.timer);
      this.mountedNodes?.forEach((el) => el.remove());
      this.started = false;
    }
    clone(name) {
      const node = this.querySelector(`template[data-mix3-template="${name}"]`)?.content.firstElementChild.cloneNode(true);
      if (node) this.mountedNodes.add(node);
      return node;
    }
    setBrowse(node, chassis) {
      const link = node.querySelector('[data-mix3-browse]');
      if (!link) return;
      const href = `${this.config.collectionUrl}${chassis ? `/${chassis[0]}` : ''}`;
      const text = chassis ? this.config.strings.browseChassis.replace('__CHASSIS__', chassis[1]) : this.config.strings.browse;
      if (link.getAttribute('href') !== href) link.setAttribute('href', href);
      if (link.textContent !== text) link.textContent = text;
    }
    mount() {
      if (!this.isConnected) return;
      const main = document.querySelector('#MainContent');
      if (!main) return;
      const hero = main.querySelector('.rdtn-hero');
      if (this.config.showHome && hero && !main.querySelector('[data-rdtn-mix3-placement="home"]')) {
        const home = this.clone('home');
        const anchor = hero.closest('.shopify-section') || hero;
        if (home) anchor.insertAdjacentElement('afterend', home);
      }
      if (this.config.showProduct && this.config.productEligible) {
        main.querySelectorAll(`product-form-component[data-product-id="${this.config.productId}"]`).forEach((form) => {
          if (form.nextElementSibling?.matches('[data-rdtn-mix3-placement="product"]')) return;
          const pdp = this.clone('product');
          if (!pdp) return;
          this.setBrowse(pdp, getChassis(this.config.productTitle));
          form.insertAdjacentElement('afterend', pdp);
        });
      }
      if (this.config.showCart && Number(this.cart?.item_count) > 0) {
        document.querySelectorAll('.cart__ctas').forEach((ctas) => {
          if (ctas.previousElementSibling?.matches('[data-rdtn-mix3-placement="cart"]')) return;
          const status = this.clone('cart');
          if (status) ctas.insertAdjacentElement('beforebegin', status);
        });
        this.paintCart();
      } else document.querySelectorAll('[data-rdtn-mix3-placement="cart"]').forEach((node) => node.remove());
    }
    paintCart() {
      const count = Math.max(0, Number(this.cart?.item_count) || 0);
      const spendReady = this.cart?.currency === 'USD' && Number(this.cart.total_price) >= 15900;
      const strings = this.config.strings;
      const message = spendReady ? strings.spendReady : count >= 3 ? strings.ready : count === 2 ? strings.addOne : strings.addMore.replace('__COUNT__', String(3 - count));
      const items = this.cart?.items || [];
      const models = items.map((item) => getChassis(item.product_title, item.variant_title)).filter(Boolean);
      const keys = new Set(models.map((model) => model[0]));
      const chassis = keys.size === 1 ? models[0] : null;
      document.querySelectorAll('[data-rdtn-mix3-placement="cart"]').forEach((node) => {
        const label = node.querySelector('[data-mix3-message]');
        if (label.textContent !== message) label.textContent = message;
        const progress = node.querySelector('[data-mix3-progress]');
        const value = Math.min(3, count);
        if (progress.value !== value) progress.value = value;
        progress.hidden = spendReady;
        const valueText = strings.countLabel.replace('__COUNT__', String(value));
        if (progress.getAttribute('aria-valuetext') !== valueText) progress.setAttribute('aria-valuetext', valueText);
        const terms = node.querySelector('[data-mix3-terms]');
        if (terms.textContent !== strings.terms) terms.textContent = strings.terms;
        this.setBrowse(node, chassis);
        node.querySelector('[data-mix3-browse]').hidden = count >= 3 || spendReady;
      });
    }
    queueRefresh() { clearTimeout(this.timer); this.timer = setTimeout(() => this.refresh(), 160); }
    async refresh() {
      const serial = ++this.serial;
      this.request?.abort();
      this.request = new AbortController();
      try {
        const root = this.config.root.endsWith('/') ? this.config.root : `${this.config.root}/`;
        const response = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: this.request.signal });
        if (!response.ok) throw new Error('Cart response unavailable');
        const cart = await response.json();
        if (serial !== this.serial || !this.isConnected || !Array.isArray(cart.items)) return;
        this.cart = cart;
        this.mount();
      } catch (error) {
        if (error.name !== 'AbortError' && serial === this.serial) {
          this.cart = null;
          document.querySelectorAll('[data-rdtn-mix3-placement="cart"]').forEach((node) => node.remove());
        }
      }
    }
  }
  customElements.define('rdtn-mix3-campaign', RenditionMix3Campaign);
})();
